import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  CircularProgress
} from '@mui/material';
import {
  GetApp as ExportIcon,
  TableChart as TableIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import api from '../../services/api';
import jsPDF from 'jspdf';
import { logger } from '../../utils/logger';

interface ExportAnalyticsProps {
  dateRange: { start: Date | null; end: Date | null };
}

const ExportAnalytics: React.FC<ExportAnalyticsProps> = ({ dateRange }) => {
  const [exporting, setExporting] = useState(false);
  const [format, setFormat] = useState<'csv' | 'pdf'>('csv');

  const fetchAnalyticsData = async () => {
    // Build query parameters with date range
    const params = new URLSearchParams();
    if (dateRange.start && dateRange.end) {
      params.append('start_date', dateRange.start.toISOString().split('T')[0]);
      params.append('end_date', dateRange.end.toISOString().split('T')[0]);
    }

    const url = `/analytics/overview${params.toString() ? '?' + params.toString() : ''}`;
    const response = await api.get(url);
    return response.data;
  };

  const exportToCSV = async () => {
    setExporting(true);
    setFormat('csv');
    try {
      const apiData = await fetchAnalyticsData();

      const csvData = [
        ['OmniPass Analitika Izveštaj - CSV Format'],
        ['Generisan:', new Date().toLocaleDateString('sr-RS')],
        [''],
        ['PREGLED'],
        ['Metrika', 'Vrednost'],
        ['Kreirani Pass-ovi', apiData.total_passes || 0],
        ['Instalirani Pass-ovi', apiData.active_passes || 0],
        ['Poništeni Pass-ovi', apiData.invalidated_passes || 0],
        ['Apple Wallet', apiData.apple_wallet || 0],
        ['Google Wallet', apiData.google_wallet || 0],
        [''],
        ['DNEVNI PODACI'],
        ['Datum', 'Kreirani', 'Instalirani', 'Poništeni'],
        ...(apiData.chart_data || []).map((row: any) => [
          row.date,
          row.created || 0,
          row.installed || 0,
          row.invalidated || 0
        ])
      ];

      const csvContent = csvData.map(row => row.join(',')).join('\n');
      downloadFile(csvContent, 'analitika-izvestaj.csv', 'text/csv');
    } catch (error) {
      logger.error('CSV export failed:', error);
      alert('Greška pri kreiranju CSV fajla');
    } finally {
      setExporting(false);
    }
  };

  const exportToPDF = async () => {
    setExporting(true);
    setFormat('pdf');
    try {
      const apiData = await fetchAnalyticsData();

      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 20;
      const maxY = pageHeight - margin;
      let yPosition = 30;

      const checkPageBreak = (requiredSpace = 20) => {
        if (yPosition + requiredSpace > maxY) {
          doc.addPage();
          yPosition = 30;
          return true;
        }
        return false;
      };

      // PDF Header
      doc.setFontSize(20);
      doc.text('OmniPass Analitika Izveštaj', margin, yPosition);
      yPosition += 15;

      doc.setFontSize(12);
      doc.text(`Generisan: ${new Date().toLocaleDateString('sr-RS')}`, margin, yPosition);
      yPosition += 20;

      // Summary Section
      checkPageBreak(100);
      doc.setFontSize(16);
      doc.text('PREGLED', margin, yPosition);
      yPosition += 15;

      doc.setFontSize(12);
      doc.text(`Kreirani Pass-ovi: ${(apiData.total_passes || 0).toLocaleString()}`, margin, yPosition);
      yPosition += 10;
      doc.text(`Instalirani Pass-ovi: ${(apiData.active_passes || 0).toLocaleString()}`, margin, yPosition);
      yPosition += 10;
      doc.text(`Poništeni: ${(apiData.invalidated_passes || 0).toLocaleString()}`, margin, yPosition);
      yPosition += 10;
      doc.text(`Apple Wallet: ${(apiData.apple_wallet || 0).toLocaleString()}`, margin, yPosition);
      yPosition += 10;
      doc.text(`Google Wallet: ${(apiData.google_wallet || 0).toLocaleString()}`, margin, yPosition);
      yPosition += 20;

      // Add footer with page numbers if multiple pages
      const totalPages = doc.internal.getNumberOfPages();
      if (totalPages > 1) {
        for (let i = 1; i <= totalPages; i++) {
          doc.setPage(i);
          doc.setFontSize(10);
          doc.text(`Strana ${i} od ${totalPages}`, pageWidth - margin - 30, pageHeight - 10);
        }
      }

      doc.save('analitika-izvestaj.pdf');
    } catch (error) {
      logger.error('PDF export failed:', error);
      alert('Greška pri kreiranju PDF-a');
    } finally {
      setExporting(false);
    }
  };

  const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box>
            <Typography variant="h6" fontWeight={600} display="flex" alignItems="center" gap={1}>
              <ExportIcon />
              Izvoz Analitike
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Preuzmite izveštaj sa pravim podacima iz sistema
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<TableIcon />}
            onClick={exportToCSV}
            disabled={exporting}
            fullWidth
          >
            {exporting && format === 'csv' ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            CSV Tabela
          </Button>
          <Button
            variant="outlined"
            startIcon={<PdfIcon />}
            onClick={exportToPDF}
            disabled={exporting}
            fullWidth
          >
            {exporting && format === 'pdf' ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            PDF Izveštaj
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ExportAnalytics;
